import React, { useState, useEffect, useMemo } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { X, Loader2 } from 'lucide-react';
import Button from '../ui/Button';
import Input from '../ui/Input';
import Select from '../ui/Select';
import { useAppStore } from '../../store';
import { formatCurrency } from '../../utils/formatters';
import ErrorModal from './ErrorModal';
import { supabase } from '../../lib/supabase';
import { getCurrentArgentinaISOString, formatArgentinaDate } from '../../utils/dateUtils';

interface CreditNoteModalProps {
  isOpen: boolean;
  onClose: () => void;
  entityId: string;
  entityName: string;
  entityType: 'client' | 'supplier';
}

const CreditNoteModal: React.FC<CreditNoteModalProps> = ({
  isOpen,
  onClose,
  entityId,
  entityName,
  entityType,
}) => {
  const { invoices, payments, addPayment } = useAppStore();
  const [selectedInvoice, setSelectedInvoice] = useState('');
  const [amount, setAmount] = useState('');
  const [date, setDate] = useState(getCurrentArgentinaISOString());
  const [method, setMethod] = useState<'cash' | 'transfer' | 'card' | 'check' | 'mercadopago'>('cash');
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isPendingInvoicesLoading, setIsPendingInvoicesLoading] = useState(false);
  const [pendingInvoicesData, setPendingInvoicesData] = useState<any[]>([]);
  
  // Reset form when modal opens
  useEffect(() => {
    if (isOpen) {
      setSelectedInvoice('');
      setAmount('');
      setDate(getCurrentArgentinaISOString());
      setMethod('cash');
      setReason('');
      
      // Fetch pending invoices directly from the database
      fetchPendingInvoices();
    }
  }, [isOpen, entityId]);
  
  // Fetch pending invoices directly from the database
  const fetchPendingInvoices = async () => {
    setIsPendingInvoicesLoading(true);
    try {
      const { data, error } = await supabase
        .from('invoices')
        .select('*')
        .eq('entity_id', entityId)
        .gt('balance', 0)
        .eq('voided', false)
        .order('date', { ascending: false });
      
      if (error) throw error;
      
      setPendingInvoicesData(data || []);
      
      // If there's only one invoice, select it automatically
      if (data && data.length === 1) {
        setSelectedInvoice(data[0].id);
        
        // Calculate the current balance for this invoice
        const invoicePayments = payments.filter(
          p => p.invoice_id === data[0].id && !p.voided
        );
        const totalPaid = invoicePayments.reduce((sum, p) => sum + p.amount, 0);
        const currentBalance = Math.max(0, data[0].total - totalPaid);
        
        setAmount(currentBalance.toString());
      }
    } catch (err) {
      console.error('Error fetching pending invoices:', err);
      setError('Error al cargar las facturas pendientes');
      setShowErrorModal(true);
    } finally {
      setIsPendingInvoicesLoading(false);
    }
  };
  
  // Get pending invoices for this entity
  const pendingInvoices = useMemo(() => {
    // Use the directly fetched data if available
    if (pendingInvoicesData.length > 0) {
      return pendingInvoicesData;
    }
    
    // Fallback to filtered data from the store
    return invoices.filter(invoice => 
      invoice.entity_id === entityId && 
      invoice.balance > 0 &&
      !invoice.voided
    );
  }, [invoices, entityId, pendingInvoicesData]);
  
  // Get the selected invoice data
  const selectedInvoiceData = useMemo(() => 
    pendingInvoices.find(inv => inv.id === selectedInvoice),
    [pendingInvoices, selectedInvoice]
  );
  
  // Get the current balance for the selected invoice
  const getCurrentInvoiceBalance = useMemo(() => {
    if (!selectedInvoiceData) return 0;
    
    const invoicePayments = payments.filter(
      p => p.invoice_id === selectedInvoiceData.id && !p.voided
    );
    const totalPaid = invoicePayments.reduce((sum, p) => sum + p.amount, 0);
    return Math.max(0, selectedInvoiceData.total - totalPaid);
  }, [selectedInvoiceData, payments]);
  
  // Validate amount
  const isAmountValid = useMemo(() => {
    if (!amount) return false;
    const numAmount = parseFloat(amount);
    return !isNaN(numAmount) && numAmount > 0;
  }, [amount]);
  
  const handleInvoiceChange = (invoiceId: string) => {
    setSelectedInvoice(invoiceId);
    
    // Find the invoice in the filtered list of invoices with balance
    const invoice = pendingInvoices.find(inv => inv.id === invoiceId && getInvoiceBalanceById(inv.id) > 0);
    if (invoice) {
      // Calculate the current balance for this invoice
      const invoicePayments = payments.filter(
        p => p.invoice_id === invoice.id && !p.voided
      );
      const totalPaid = invoicePayments.reduce((sum, p) => sum + p.amount, 0);
      const currentBalance = Math.max(0, invoice.total - totalPaid);
      
      // Set the amount to the current balance
      setAmount(currentBalance.toString());
    } else {
      setAmount('');
    }
  };

  // Helper function to get current balance for an invoice
  const getInvoiceBalanceById = (invoiceId: string): number => {
    const invoice = pendingInvoices.find(inv => inv.id === invoiceId);
    if (!invoice) return 0;
    
    const invoicePayments = payments.filter(
      p => p.invoice_id === invoice.id && !p.voided
    );
    const totalPaid = invoicePayments.reduce((sum, p) => sum + p.amount, 0);
    return Math.max(0, invoice.total - totalPaid);
  };

  const handleAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (value === '') {
      setAmount('');
      return;
    }

    const numValue = parseFloat(value);
    if (!isNaN(numValue) && numValue > 0) {
      setAmount(value);
    }
  };

  const handlePercentageClick = (percentage: number) => {
    if (selectedInvoiceData) {
      const currentBalance = getCurrentInvoiceBalance;
      if (currentBalance > 0) {
        const calculatedAmount = (currentBalance * percentage) / 100;
        setAmount(calculatedAmount.toFixed(2));
      }
    }
  };
  
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!selectedInvoice || !amount) {
      setError('Debe seleccionar una factura y especificar un monto');
      setShowErrorModal(true);
      return;
    }

    const creditAmount = parseFloat(amount);
    if (isNaN(creditAmount) || creditAmount <= 0 || !selectedInvoice) {
      setError('El monto debe ser mayor a 0');
      setShowErrorModal(true);
      return;
    }
    
    // Check if the invoice has enough balance for this credit note
    const invoiceBalance = getInvoiceBalanceById(selectedInvoice);
    if (creditAmount > invoiceBalance) {
      setError(`El monto de la nota de crédito (${formatCurrency(creditAmount)}) no puede ser mayor al saldo de la factura (${formatCurrency(invoiceBalance)})`);
      setShowErrorModal(true);
      return;
    }

    setIsLoading(true);
    
    try {
      // Create a credit note (negative payment)
      const creditNote = {
        date,
        invoice_id: selectedInvoice,
        amount: creditAmount, // Store as positive amount
        method,
        reference: reason.trim() || 'Nota de crédito',
        invoice_type: 'credit_note',
      };
      
      await addPayment(creditNote);
      onClose();
    } catch (error: any) {
      console.error('Error al emitir la nota de crédito:', error);
      setError('Ocurrió un error al emitir la nota de crédito. Por favor, intente nuevamente.');
      setShowErrorModal(true);
    } finally {
      setIsLoading(false);
    }
  };
  
  // Prepare invoice options with validation
  const invoiceOptions = useMemo(() => {
    if (isPendingInvoicesLoading) {
      return [{ value: 'loading', label: 'Cargando facturas...', disabled: true }];
    }
    
    // Filter out invoices with zero balance
    const invoicesWithBalance = pendingInvoices.filter(invoice => {
      // Calculate the current balance for this invoice
      const invoicePayments = payments.filter(
        p => p.invoice_id === invoice.id && !p.voided
      );
      const totalPaid = invoicePayments.reduce((sum, p) => sum + p.amount, 0);
      const currentBalance = Math.max(0, invoice.total - totalPaid);
      
      // Only include invoices with positive balance
      return currentBalance > 0;
    });
    
    if (invoicesWithBalance.length === 0) {
      return [{ value: 'no-invoices', label: 'No hay facturas pendientes', disabled: true }];
    }
    
    return [
      { value: '', label: 'Seleccionar factura...', disabled: true },
      ...invoicesWithBalance.map(invoice => {
        // Calculate the current balance for this invoice
        const invoicePayments = payments.filter(
          p => p.invoice_id === invoice.id && !p.voided
        );
        const totalPaid = invoicePayments.reduce((sum, p) => sum + p.amount, 0);
        const currentBalance = Math.max(0, invoice.total - totalPaid);
        
        return {
          value: invoice.id,
          label: `${invoice.number} - ${formatArgentinaDate(invoice.date)} (Saldo: ${formatCurrency(currentBalance)})`,
        };
      })
    ];
  }, [pendingInvoices, payments, isPendingInvoicesLoading]);
  
  return (
    <>
      <Dialog.Root open={isOpen} onOpenChange={onClose}>
        <Dialog.Portal>
          <Dialog.Overlay className="fixed inset-0 bg-black/50" />
          <Dialog.Content className="fixed left-[50%] top-[50%] max-h-[85vh] w-[90vw] max-w-[500px] translate-x-[-50%] translate-y-[-50%] rounded-lg bg-white dark:bg-gray-800 p-6 shadow-lg">
            <Dialog.Title className="text-xl font-semibold mb-4 text-gray-900 dark:text-white flex items-center">
              Emitir Nota de Crédito
            </Dialog.Title>
            
            <form onSubmit={handleSubmit}>
              <div className="space-y-4">
                {isPendingInvoicesLoading ? (
                  <div className="flex justify-center items-center py-4">
                    <Loader2 className="h-6 w-6 text-indigo-600 dark:text-indigo-400 animate-spin mr-2" />
                    <span className="text-gray-700 dark:text-gray-300">Cargando facturas pendientes...</span>
                  </div>
                ) : pendingInvoices.length === 0 ? (
                  <div className="bg-amber-50 dark:bg-amber-900/30 p-4 rounded-md text-amber-800 dark:text-amber-300 mb-4">
                    <p className="font-medium">No hay facturas pendientes</p>
                    <p className="text-sm mt-1">
                      Este {entityType === 'client' ? 'cliente' : 'proveedor'} no tiene facturas con saldo pendiente.
                    </p>
                  </div>
                ) : (
                  <Select
                    label="Factura"
                    value={selectedInvoice}
                    onChange={handleInvoiceChange}
                    options={invoiceOptions}
                    required
                  />
                )}
                
                <Input
                  type="date"
                  label="Fecha"
                  id="date"
                  name="date"
                  value={date}
                  onChange={(e) => setDate(e.target.value)}
                  required
                />
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Monto
                    <span className="text-red-500 ml-1">*</span>
                  </label>
                  <div className="flex items-center gap-2">
                    <input
                      type="number"
                      placeholder="Monto a devolver"
                      id="amount"
                      name="amount"
                      value={amount}
                      onChange={handleAmountChange}
                      required
                      min="0.01"
                      step="0.01"
                      className="flex-1 h-12 px-3 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 dark:bg-gray-700 dark:text-white sm:text-sm"
                      disabled={!selectedInvoice || isPendingInvoicesLoading}
                    />
                    <div className="flex gap-1">
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => handlePercentageClick(40)}
                        disabled={!selectedInvoice || isPendingInvoicesLoading}
                        className="h-12 px-4 flex items-center justify-center"
                      >
                        40%
                      </Button>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => handlePercentageClick(50)}
                        disabled={!selectedInvoice || isPendingInvoicesLoading}
                        className="h-12 px-4 flex items-center justify-center"
                      >
                        50%
                      </Button>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => handlePercentageClick(100)}
                        disabled={!selectedInvoice || isPendingInvoicesLoading}
                        className="h-12 px-4 flex items-center justify-center"
                      >
                        100%
                      </Button>
                    </div>
                  </div>
                </div>
                
                {selectedInvoiceData && amount && parseFloat(amount) > 0 && (
                  <div>
                    <p className="text-sm text-amber-600 dark:text-amber-400">
                      Se emitirá una nota de crédito por {formatCurrency(parseFloat(amount))}
                    </p>
                  </div>
                )}
                
                <Select
                  label="Método de Pago"
                  value={method}
                  onChange={(value) => setMethod(value as typeof method)}
                  options={[
                    { value: 'cash', label: 'Efectivo' },
                    { value: 'transfer', label: 'Transferencia' },
                    { value: 'card', label: 'Tarjeta' },
                    { value: 'check', label: 'Cheque' },
                    { value: 'mercadopago', label: 'MercadoPago' },
                  ]}
                  required
                />
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Motivo de la Nota de Crédito
                  </label>
                  <textarea
                    id="reason"
                    name="reason"
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    placeholder="Describa el motivo de la nota de crédito"
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 dark:bg-gray-700 dark:text-white sm:text-sm"
                    rows={3}
                  />
                </div>
              </div>
              
              <div className="mt-6 flex justify-end gap-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={onClose}
                  disabled={isLoading}
                >
                  Cancelar
                </Button>
                
                <Button
                  type="submit"
                  disabled={!selectedInvoice || !isAmountValid || isLoading || isPendingInvoicesLoading}
                >
                  {isLoading ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin mr-2" />
                      Procesando...
                    </>
                  ) : 'Emitir Nota de Crédito'}
                </Button>
              </div>
            </form>
            
            <Dialog.Close asChild>
              <button
                className="absolute top-4 right-4 text-gray-400 hover:text-gray-500 dark:hover:text-gray-300"
                aria-label="Close"
                disabled={isLoading}
              >
                <X className="h-6 w-6" />
              </button>
            </Dialog.Close>
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>

      <ErrorModal
        isOpen={showErrorModal}
        onClose={() => setShowErrorModal(false)}
        message={error || 'Ha ocurrido un error'}
      />
    </>
  );
};

export default CreditNoteModal;